import { Link } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { StageBadge } from '@/components/shared/StageBadge'
import { formatCurrency } from '@/lib/utils'
import { useBasePath } from '@/lib/paths'
import { ProgressStepper } from '@/components/shared/ProgressStepper'
import { CreditCard, FileText, ChevronRight } from 'lucide-react'
import { FieldValues } from '@/components/applicant/FieldValues'
import type { FieldDefinition, FieldValueData } from '@/components/applicant/FieldValues'

const STAGE_KEYS = ['application', 'payment', 'active', 'ended'] as const
const STAGE_LABELS: Record<string, string> = { application: 'Application', payment: 'Paid', active: 'Active', ended: 'Ended' }


type RegisteredAddressDetailed = {
  id: string; stage: string; type_name: string; jurisdiction_label: string;
  business_name: string; duration_months: number; created_at: string;
  activated_at: string | null; ended_at: string | null;
  documents_count: number;
  payment: {
    id: string; service_fee_cents: number; gov_fee_cents: number; total_cents: number;
    currency: string; paid: boolean; paid_at: string | null;
    invoice_number: string | null; receipt_number: string | null
  } | null
}
type TypeSummary = {
  id: string; name: string; jurisdiction_code: string; jurisdiction_label: string;
  gov_fee_cents: number; service_fee_cents: number; min_duration_months: number;
  processing_days_min: number | null; processing_days_max: number | null;
}

type Props = {
  registered_address: RegisteredAddressDetailed
  type: TypeSummary
  fields: FieldDefinition[]
  field_values: FieldValueData[]
}

export default function RegisteredAddressShow({ registered_address, type, fields, field_values }: Props) {
  const basePath = useBasePath()
  const serviceFee = Math.round(type.service_fee_cents * registered_address.duration_months / 12)
  const govFee = Math.round(type.gov_fee_cents * registered_address.duration_months / 12)
  const totalCost = serviceFee + govFee

  return (
    <DashboardLayout title={registered_address.business_name} breadcrumbs={[{ label: 'Registered Address', href: 'registered_addresses' }, { label: registered_address.business_name }]}>
      <div>
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl font-semibold tracking-tight">{registered_address.business_name}</h1>
            <StageBadge stage={registered_address.stage} variant="service" />
          </div>
          <p className="text-sm text-muted-foreground">
            {type.name} · {type.jurisdiction_label} · {registered_address.duration_months} months
          </p>
          <div className="flex items-center gap-2 mt-3">
            <Button asChild size="sm" variant="outline">
              <Link href={`${basePath}/registered_addresses/${registered_address.id}/documents`}>
                <FileText className="w-3.5 h-3.5 mr-1.5" />
                Documents
                {registered_address.documents_count > 0 && (
                  <Badge variant="secondary" className="ml-1.5 font-normal text-xs px-1.5 py-0">{registered_address.documents_count}</Badge>
                )}
              </Link>
            </Button>
            <Button asChild size="sm" variant="outline">
              <Link href={`${basePath}/registered_addresses/${registered_address.id}/payment`}>
                <CreditCard className="w-3.5 h-3.5 mr-1.5" />
                Payment
                {registered_address.payment?.paid && (
                  <Badge className="ml-1.5 font-normal text-xs px-1.5 py-0 bg-green-600 text-white">Paid</Badge>
                )}
              </Link>
            </Button>
          </div>
        </div>

        <div className="mb-8 py-4 px-5 border rounded-lg">
          <ProgressStepper stages={STAGE_KEYS} labels={STAGE_LABELS} currentStage={registered_address.stage} />
        </div>

        {/* Get Started callout - shows when payment not yet made */}
        {(() => {
          const missing: { label: string; description: string; href: string; icon: React.ReactNode }[] = []
          if (!registered_address.payment || !registered_address.payment.paid) missing.push({ label: 'Complete payment', description: `Pay ${formatCurrency(totalCost)} to activate your registered address`, href: `${basePath}/registered_addresses/${registered_address.id}/payment`, icon: <CreditCard className="w-4 h-4 text-muted-foreground" /> })
          if (missing.length === 0) return null
          return (
            <div className="mb-8">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Get Started</h2>
              <div className="rounded-lg border border-dashed bg-muted/30 divide-y divide-dashed">
              {missing.map(item => (
                <Link
                  key={item.label}
                  href={item.href}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                    {item.icon}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-foreground">{item.label}</div>
                    <div className="text-xs text-muted-foreground">{item.description}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </Link>
              ))}
              </div>
            </div>
          )
        })()}

        <div className="space-y-6">
          {fields.length > 0 && (
            <section className="border rounded-lg px-4 py-3">
              <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-3">Form Data</p>
              <FieldValues fields={fields} values={field_values} />
            </section>
          )}
        </div>
      </div>
    </DashboardLayout>
  )
}
